import { NavLink, useNavigate } from 'react-router';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import { useTranslation } from 'react-i18next';
import type { AuthUser } from '../utils/auth';
import { hasRole, logoutUser } from '../utils/auth';

interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
  currentUser: AuthUser | null;
}

const listItemStyles = {
  color: 'white',
  '& .MuiListItemText-primary': {
    color: 'white',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    cursor: 'pointer',
    '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
      color: '#8900e1',
    },
  },
};

export default function NavDrawer({ open, onClose, currentUser }: NavDrawerProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = async () => {
    onClose();
    const logoutRoute = await logoutUser();
    navigate(logoutRoute, { replace: true });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          backgroundColor: '#330662',
          color: 'white',
        },
      }}
    >
      <Box
        sx={{ width: 250 }}
        role="presentation"
        onClick={onClose}
        onKeyDown={onClose}
      >
        <List>
          {currentUser && (hasRole(currentUser, 'app') || hasRole(currentUser, 'admin')) && (
            <ListItemButton component={NavLink} to="/application" sx={listItemStyles}>
              <ListItemIcon>
                <ArticleIcon sx={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary={t('Application', 'Application')} />
            </ListItemButton>
          )}

          {currentUser && (hasRole(currentUser, 'hha') || hasRole(currentUser, 'admin')) && (
            <ListItemButton component={NavLink} to="/onboarding" sx={listItemStyles}>
              <ListItemIcon>
                <ArticleIcon sx={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary={t('Onboarding', 'Onboarding')} />
            </ListItemButton>
          )}

          {currentUser && hasRole(currentUser, 'admin') && (
            <ListItemButton component={NavLink} to="/templates" sx={listItemStyles}>
              <ListItemIcon>
                <DescriptionIcon sx={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary={t('Templates', 'Templates')} />
            </ListItemButton>
          )}

          {currentUser && (hasRole(currentUser, 'nurse') || hasRole(currentUser, 'nurse-admin') || hasRole(currentUser, 'admin')) && (
            <ListItemButton component={NavLink} to="/nursing-assessment" sx={listItemStyles}>
              <ListItemIcon>
                <AssignmentIcon sx={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary={t('Nursing Assessment', 'Nursing Assessment')} />
            </ListItemButton>
          )}

          {currentUser && hasRole(currentUser, 'admin') && (
            <ListItemButton component={NavLink} to="/documents" sx={listItemStyles}>
              <ListItemIcon>
                <FolderIcon sx={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary={t('Documents', 'Documents')} />
            </ListItemButton>
          )}

          {currentUser && (hasRole(currentUser, 'quick-entry') || hasRole(currentUser, 'admin')) && (
            <>
              <ListItemButton component={NavLink} to="/qe-landing" sx={listItemStyles}>
                <ListItemIcon>
                  <PersonAddIcon sx={{ color: 'white' }} />
                </ListItemIcon>
                <ListItemText primary={t('nav.qeLeads', 'QE Leads')} />
              </ListItemButton>
              <ListItemButton component={NavLink} to="/quick-entry" sx={listItemStyles}>
                <ListItemIcon>
                  <PersonAddIcon sx={{ color: 'white' }} />
                </ListItemIcon>
                <ListItemText primary={t('nav.qeNewLead', 'QE New Lead')} />
              </ListItemButton>
            </>
          )}

          {currentUser && (hasRole(currentUser, 'referral') || hasRole(currentUser, 'admin')) && (
            <ListItemButton component={NavLink} to="/referral" sx={listItemStyles}>
              <ListItemIcon>
                <GroupsIcon sx={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary={t('nav.referralPortal', 'Referral Portal')} />
            </ListItemButton>
          )}

          <ListItemButton onClick={handleLogout} sx={listItemStyles}>
            <ListItemIcon>
              <LogoutIcon sx={{ color: 'white' }} />
            </ListItemIcon>
            <ListItemText primary={t('register.logoutButton', 'Log Out')} />
          </ListItemButton>
        </List>
      </Box>
    </Drawer>
  );
}
